import React, { useState } from "react";
import { Eye, Edit3, Trash, ChevronDown, ChevronUp } from "lucide-react";
import AddEditCustomerModal from "../components/AddEditCustomerModal";
import CustomerDetailsModal from "../components/CustomerDetailsModal";

const initialCustomers = [
  {
    id: 1,
    first_name: "John",
    last_name: "Doe",
    email: "john.doe@example.com",
    phone: "123-456-7890",
    address: "123 Main St, Springfield, USA",
    organization: "ABC Corp",
    type: "Individual",
    status: "Active",
    totalOrders: 15,
    totalRevenue: 3000,
  },
  {
    id: 2,
    first_name: "Jane",
    last_name: "Smith",
    email: "jane.smith@example.com",
    phone: "987-654-3210",
    address: "456 Elm St, Springfield, USA",
    organization: "XYZ Ltd",
    type: "Business",
    status: "Inactive",
    totalOrders: 5,
    totalRevenue: 1200,
  },
];

function CustomerDashboard() {
  const [customers, setCustomers] = useState(initialCustomers);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [expandedCustomerId, setExpandedCustomerId] = useState(null);

  // Filtered customers
  const filteredCustomers = customers
    .filter((customer) =>
      `${customer.first_name} ${customer.last_name}`
        .toLowerCase()
        .includes(searchQuery.toLowerCase())
    )
    .filter((customer) =>
      selectedStatus ? customer.status === selectedStatus : true
    );

  // Add new customer
  const handleAddCustomer = (newCustomer) => {
    setCustomers([...customers, { id: customers.length + 1, ...newCustomer }]);
    setIsModalOpen(false);
  };

  // Edit customer
  const handleEditCustomer = (updatedCustomer) => {
    setCustomers(
      customers.map((customer) =>
        customer.id === updatedCustomer.id ? updatedCustomer : customer
      )
    );
    setIsModalOpen(false);
  };

  // Delete customer
  const handleDeleteCustomer = (customerId) => {
    if (window.confirm("Are you sure you want to delete this customer?")) {
      setCustomers(customers.filter((customer) => customer.id !== customerId));
    }
  };

  // Open add modal
  const openAddModal = () => {
    setIsEditing(false);
    setSelectedCustomer(null);
    setIsModalOpen(true);
  };

  // Open edit modal
  const openEditModal = (customer) => {
    setIsEditing(true);
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  // Toggle expand/collapse for customer details
  const toggleExpand = (customerId) => {
    setExpandedCustomerId(expandedCustomerId === customerId ? null : customerId);
  };

  return (
    <div className="relative p-6 bg-gray-100 min-h-screen">
      {/* Header Section */}
      <div className="flex flex-col md:flex-row justify-between items-center mb-4">
  <h2 className="text-lg md:text-2xl font-semibold text-gray-800 mb-2 md:mb-0">
    Customer Management
  </h2>
  <button
    onClick={openAddModal}
    className="bg-blue-600 text-white px-3 py-2 text-sm rounded-md hover:bg-blue-500"
  >
    + Add Customer
  </button>
</div>

      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
        <input
          type="text"
          placeholder="Search customers by name..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="border p-2 rounded-md w-full md:w-1/2"
        />
        <select
          className="border p-2 rounded-md"
          value={selectedStatus}
          onChange={(e) => setSelectedStatus(e.target.value)}
        >
          <option value="">All Statuses</option>
          <option value="Active">Active</option>
          <option value="Inactive">Inactive</option>
          <option value="Blacklisted">Blacklisted</option>
        </select>
      </div>

      {/* Customers Table */}
      <div className="overflow-x-auto bg-white rounded-lg shadow-md pb-6">
      <table className="min-w-full table-auto text-sm">
          <thead className="bg-gray-100 text-left">
            <tr>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Phone</th>
              <th className="px-4 py-2">Organization</th>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCustomers.map((customer) => (
              <React.Fragment key={customer.id}>
                <tr className="border-t hover:bg-gray-50">
                <td className="px-4 py-2 truncate max-w-[150px]">{`${customer.first_name} ${customer.last_name}`}</td>
<td className="px-4 py-2 truncate max-w-[180px]">{customer.email}</td>
<td className="px-4 py-2 truncate max-w-[120px]">{customer.phone}</td>
<td className="px-4 py-2 truncate max-w-[150px]">{customer.organization}</td>
<td className="px-4 py-2 text-center">
  <span
    className={`px-2 py-1 rounded-full text-sm ${
      customer.status === "Active"
        ? "bg-green-100 text-green-600"
        : customer.status === "Inactive"
        ? "bg-yellow-100 text-yellow-600"
        : "bg-red-100 text-red-600"
    }`}
  >
    {customer.status}
  </span>
</td>
                  <td className="px-2 py-2 flex items-center justify-center space-x-1">
  <button onClick={() => toggleExpand(customer.id)} className="text-gray-600 hover:text-gray-800 p-1">
    {expandedCustomerId === customer.id ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
  </button>
  <button onClick={() => openEditModal(customer)} className="text-green-600 hover:text-green-800 p-1">
    <Edit3 size={14} />
  </button>
  <button onClick={() => handleDeleteCustomer(customer.id)} className="text-red-600 hover:text-red-800 p-1">
    <Trash size={14} />
  </button>
</td>                </tr>
                {expandedCustomerId === customer.id && (
                  <tr>
                    <td className="px-4 py-2 bg-gray-100" colSpan="6">
                      <p>
                        <strong>Address:</strong> {customer.address}
                      </p>
                      <p>
                        <strong>Total Orders:</strong> {customer.totalOrders}
                      </p>
                      <p>
                        <strong>Total Revenue:</strong> $
                        {customer.totalRevenue.toFixed(2)}
                      </p>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Customer Modal */}
      {isModalOpen && (
        <AddEditCustomerModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onAddCustomer={handleAddCustomer}
          onEditCustomer={handleEditCustomer}
          isEditing={isEditing}
          customer={selectedCustomer}
        />
      )}
    </div>
  );
}

export default CustomerDashboard;